import * as THREE from 'three';
import gsap from 'gsap';
import Behavior from './Behavior.js';
import Space from '../Components/Space.js';
import Animations from '../Systems/Animations.js';

/**
 * DrawDeck behavior — a card-drawing experience.
 *
 * - All cards start as a stacked deck slightly above table center, face-down.
 * - Tap the top card → it flies into focus, turning face-up.
 * - Tap anywhere → the focused card flies to the next arc position, face-down.
 * - Tap a card in the arc → re-focuses it face-up.
 * - Continue until the deck is empty.
 */
export default class DrawDeckBehavior extends Behavior {

    // Deck position: slightly above table center
    static DECK_X = 0;
    static DECK_Y = 0.5;
    static DECK_STACK_Z_STEP = 0.002;

    // Arc layout: cards are placed along an arc below the deck
    static ARC_CENTER_X = 0;
    static ARC_CENTER_Y = -1.8;
    static ARC_RADIUS = 3.5;
    static ARC_START_ANGLE = Math.PI * 0.85;
    static ARC_END_ANGLE = Math.PI * 0.15;

    constructor(space, interaction) {
        super(space, interaction);
        this.deckCards = [];        // cards remaining in the deck (bottom to top)
        this.drawnCards = [];       // cards drawn, in draw order
        this.focusedCard = null;    // the card currently in focus (or null)
    }

    // ─── Lifecycle ────────────────────────────────────────────────────

    onSetup(cards) {
        this.deckCards = [...cards];
        this.drawnCards = [];
        this.focusedCard = null;

        // Stack all cards at deck position, face-down
        for (let i = 0; i < this.deckCards.length; i++) {
            const card = this.deckCards[i];
            const z = Space.CARD_DOWN_Z + i * DrawDeckBehavior.DECK_STACK_Z_STEP;
            card.setPosition(new THREE.Vector3(
                DrawDeckBehavior.DECK_X,
                DrawDeckBehavior.DECK_Y,
                z
            ));
            if (card.isFaceUp) {
                Animations.flipCardQuaternion(card, false);
                card.isFaceUp = false;
            }
        }
    }

    onDestroy() {
        this.deckCards = [];
        this.drawnCards = [];
        this.focusedCard = null;
    }

    // ─── Input hooks ──────────────────────────────────────────────────

    onTap(card, position) {
        // Case 1: A card is currently in focus → dismiss it
        if (this.focusedCard) {
            this._dismissFocusedCard();
            return;
        }

        // Case 2: Tapped a card in the arc → re-focus it
        if (card && this.drawnCards.includes(card)) {
            this._focusCard(card);
            return;
        }

        // Case 3: Tapped on the deck (top card) → draw it
        if (card && this.deckCards.includes(card)) {
            const topCard = this.deckCards[this.deckCards.length - 1];
            if (card.id === topCard.id) {
                this._drawCard(topCard);
            }
            return;
        }

        // Case 4: Tapped background with no card focused → nothing
    }

    onDoubleTap(card, position) {
        // DrawDeck uses single-tap only; treat double-tap the same
        this.onTap(card, position);
    }

    // Disable drag, scroll, keyboard for DrawDeck
    onPanStart() { return false; }
    onPanMove() {}
    onPanEnd() {}
    onMouseMove(card, position) {
        if (this.focusedCard) {
            this.focusedCard.tiltTowards(position.x, position.y);
        }
    }
    onScrollWheel() {}
    onKeyDown() {}
    onKeyUp() {}
    onWindowBlur() {}

    // ─── Remote event handlers ────────────────────────────────────────

    getRemoteHandlers() {
        return {
            drewCard: (data) => {
                const card = this.space.getCard(data.id);
                if (!card) return;
                this._removeFromDeck(card);
                this.drawnCards.push(card);
                this.space.reorderCards(card);
                card.focus();
                this.focusedCard = card;
                if (!card.isFaceUp) {
                    card.isFaceUp = true;
                    Animations.flipCardQuaternion(card, true);
                }
            },
            dismissedCard: (data) => {
                const card = this.space.getCard(data.id);
                if (!card) return;
                if (this.focusedCard?.id === card.id) {
                    this.focusedCard = null;
                }
                card.isFaceUp = false;
                Animations.flipCardQuaternion(card, false);
                const target = new THREE.Vector3(data.x, data.y, Space.CARD_DOWN_Z);
                this.space.animateCardMotion(card, target, {
                    duration: 0.4,
                    ease: "power1.out",
                    clearFocus: true,
                    lifted: false,
                    resetRotation: false,
                });
            },
            refocusedCard: (data) => {
                const card = this.space.getCard(data.id);
                if (!card) return;
                if (this.focusedCard && this.focusedCard.id !== card.id) {
                    this.focusedCard.isFocused = false;
                }
                this.space.reorderCards(card);
                card.focus();
                this.focusedCard = card;
                if (!card.isFaceUp) {
                    card.isFaceUp = true;
                    Animations.flipCardQuaternion(card, true);
                }
            },
        };
    }

    // ─── Private ──────────────────────────────────────────────────────

    _drawCard(card) {
        this._removeFromDeck(card);
        this.drawnCards.push(card);
        this.space.reorderCards(card);

        // Focus the card and flip face-up
        card.isFaceUp = true;
        Animations.flipCardQuaternion(card, true, null, 0.4);
        card.focus();
        this.focusedCard = card;

        this.space.eventBus.pushRemote('drewCard', {
            id: card.id,
            index: card.sortOrder,
        });
        this.space.emitCardStateChanged();
    }

    _focusCard(card) {
        this.space.reorderCards(card);

        card.isFaceUp = true;
        Animations.flipCardQuaternion(card, true, null, 0.4);
        card.focus();
        this.focusedCard = card;

        this.space.eventBus.pushRemote('refocusedCard', {
            id: card.id,
            index: card.sortOrder,
        });
        this.space.emitCardStateChanged();
    }

    _dismissFocusedCard() {
        const card = this.focusedCard;
        if (!card) return;

        this.focusedCard = null;
        const arcPos = this._getArcPosition(this.drawnCards.indexOf(card));

        card.isFaceUp = false;
        Animations.flipCardQuaternion(card, false, null, 0.3);

        const target = new THREE.Vector3(arcPos.x, arcPos.y, Space.CARD_DOWN_Z);

        const vignette = card.vignetteModifiers;
        this.space.animateCardMotion(card, target, {
            duration: 0.5,
            ease: "power2.out",
            clearFocus: true,
            lifted: false,
            resetRotation: false,
        });
        const sharpnessTarget = vignette.sharpnessInitial ?? vignette.sharpness;
        const baseAlphaTarget = vignette.baseAlphaInitial ?? vignette.baseAlpha;

        gsap.to(vignette, {
            sharpness: sharpnessTarget,
            duration: 0.4,
            delay: 0.11,
            ease: "power1.out",
            overwrite: "auto",
        });
        gsap.to(vignette, {
            baseAlpha: baseAlphaTarget,
            duration: 0.4,
            delay: 0.11,
            ease: "power1.out",
            overwrite: "auto",
        });

        this.space.eventBus.pushRemote('dismissedCard', {
            id: card.id,
            index: card.sortOrder,
            x: target.x,
            y: target.y,
        });
        this.space.emitCardStateChanged();
    }

    _removeFromDeck(card) {
        const idx = this.deckCards.indexOf(card);
        if (idx !== -1) this.deckCards.splice(idx, 1);
    }

    _getArcPosition(arcIndex) {
        const total = this.drawnCards.length;
        const t = total <= 1 ? 0.5 : arcIndex / (total - 1);
        const angle = DrawDeckBehavior.ARC_START_ANGLE +
            t * (DrawDeckBehavior.ARC_END_ANGLE - DrawDeckBehavior.ARC_START_ANGLE);

        return {
            x: DrawDeckBehavior.ARC_CENTER_X + DrawDeckBehavior.ARC_RADIUS * Math.cos(angle),
            y: DrawDeckBehavior.ARC_CENTER_Y + DrawDeckBehavior.ARC_RADIUS * Math.sin(angle),
        };
    }
}
